import { Component, input, output, forwardRef, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Radio button option interface
 */
export interface RadioOption {
  /**
   * The value of the radio option
   */
  value: string | number | boolean;

  /**
   * Translation key for the label text
   */
  labelKey: string;

  /**
   * Optional: Direct label text (if not using translation)
   */
  label?: string;

  /**
   * Whether this option is disabled
   */
  disabled?: boolean;
}

/**
 * Radio input component for single selection within a group.
 * Implements ControlValueAccessor for Angular forms integration.
 *
 * Figma reference:
 * - https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=90-11650
 */
@Component({
  selector: 'app-radio-input',
  standalone: false,
  templateUrl: './radio-input.component.html',
  styleUrls: ['./radio-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioInputComponent),
      multi: true
    }
  ]
})
export class RadioInputComponent implements ControlValueAccessor {
  /**
   * The name attribute for the radio group (required for radio button grouping)
   */
  name = input.required<string>();

  /**
   * Array of radio options to display
   */
  options = input<RadioOption[]>([]);

  /**
   * Optional description text to display above the radio group
   */
  description = input<string>();

  /**
   * Optional translation key for the description text
   */
  descriptionKey = input<string>();

  /**
   * Layout direction: 'horizontal' or 'vertical'
   */
  direction = input<'horizontal' | 'vertical'>('vertical');

  /**
   * Whether the radio group is disabled (for template-driven forms or standalone usage)
   */
  disabled = input<boolean>(false);

  /**
   * Whether the radio group is required
   */
  required = input<boolean>(false);

  /**
   * Emits when the selected value changes
   */
  valueChange = output<string | number | boolean>();

  /**
   * Internal selected value
   */
  value: string | number | boolean | null = null;

  /**
   * Internal disabled state (set by reactive forms via setDisabledState)
   */
  private isDisabledByForm = signal<boolean>(false);

  /**
   * Computed disabled state (combines input signal and form control state)
   * Reactive forms disabled state takes precedence
   */
  isDisabled = computed(() => this.isDisabledByForm() || this.disabled());

  /**
   * Touched state
   */
  private touched = false;

  /**
   * onChange callback for ControlValueAccessor
   */
  private onChange: (value: string | number | boolean | null) => void = () => {};

  /**
   * onTouched callback for ControlValueAccessor
   */
  private onTouched: () => void = () => {};

  /**
   * Check if an option is selected
   */
  isSelected(optionValue: string | number | boolean): boolean {
    return this.value === optionValue;
  }

  /**
   * Check if an option is disabled
   */
  isOptionDisabled(option: RadioOption): boolean {
    return this.isDisabled() || option.disabled === true;
  }

  /**
   * Handle radio selection change
   */
  onRadioChange(optionValue: string | number | boolean): void {
    if (this.isDisabled()) {
      return;
    }

    this.value = optionValue;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.markAsTouched();
  }

  /**
   * Mark the control as touched
   */
  private markAsTouched(): void {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  // ControlValueAccessor implementation

  writeValue(value: string | number | boolean | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | number | boolean | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabledByForm.set(isDisabled);
  }
}
